/**
 * /login — email + password form.
 *
 * Flow:
 *   1. Submit → `POST /admin/auth/login` via the auth store.
 *   2. On success → redirect to `location.state.from` or `/dashboard`.
 *   3. On error → toast + inline message. 423 (locked) surfaces the
 *      `lockedUntil` hint from the API payload.
 */

import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate, type Location } from 'react-router-dom';
import { Loader2, LogIn } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';
import { ApiError } from '@/lib/api';
import { formatDateTime } from '@/lib/format-date';
import { useAuthStore } from '@/stores/auth';

interface LocationState {
  from?: Location;
}

function extractApiMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { code?: string; message?: string; lockedUntil?: string } };
    if (body?.error?.code === 'ACCOUNT_LOCKED' && body.error.lockedUntil) {
      const until = formatDateTime(body.error.lockedUntil);
      return `Conta temporariamente bloqueada até ${until}.`;
    }
    if (body?.error?.message) {
      return body.error.message;
    }
    if (err.status === 401) {
      return 'E-mail ou senha inválidos.';
    }
    return `Erro ${err.status} ao tentar entrar.`;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return 'Erro desconhecido ao tentar entrar.';
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const accessToken = useAuthStore((s) => s.accessToken);
  const login = useAuthStore((s) => s.login);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = (location.state as LocationState | null)?.from?.pathname ?? '/dashboard';

  if (accessToken) {
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success('Bem-vindo!');
      navigate(from, { replace: true });
    } catch (err) {
      const message = extractApiMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LogIn className="size-5" aria-hidden />
            Entrar
          </CardTitle>
          <CardDescription>Acesse o painel administrativo do Sales Agent.</CardDescription>
        </CardHeader>
        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                placeholder="voce@empresa.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                minLength={1}
              />
            </div>
            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
          </CardContent>
          <CardFooter>
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="size-4 animate-spin" /> Entrando...
                </>
              ) : (
                'Entrar'
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </main>
  );
}

export default LoginPage;
